import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { Event as GlobalEvent } from "../../Global/Base/Events";
import { IDisposable } from "../../Global/Base/IDisposable";
import { Control } from "../UI.BaseControls/Control";
import { EventHelper } from "../UI.BaseControls/Helpers/EventHelper";
import { PopupMenu } from "../UI.BaseControls/PopupMenu";
import { registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { ICalendarItemData, ICalendarTooltip, ICalendarTooltipCreator } from "./ICalendarTooltip";

const logger = LazyLoadingSectionLogger.create(LoggingSection.calendarFrame);

export class CalendarTooltip extends Control implements IDisposable, ICalendarTooltip {
    public readonly opened: GlobalEvent<CalendarTooltip, void> = new GlobalEvent<CalendarTooltip, void>();
    public readonly closed: GlobalEvent<CalendarTooltip, void> = new GlobalEvent<CalendarTooltip, void>();

    private readonly _showOffsetX: number = 5; //Počet px o kterých tooltip bude posunut směrem vrapvo
    private readonly _showOffsetY: number = 25; //Počet px o kterých tooltip bude posunut směrem dolů
    protected _closingTimeout: number = 500; //Timeout pro spuštění zavření tooltipu.
    protected _openingTimeout: number = 250; //Timeout pro spuštění otevření tooltipu.

    private _windowResizeListener: EventListener;
    private _popup: PopupMenu;
    private _parentContainer: HTMLElement;
    private _eventListFnc: any = {};
    private _calendarItemData: ICalendarItemData;
    private _tooltipContent: HTMLElement;
    private _tooltipCreator: ICalendarTooltipCreator;
    private _canShowTooltipFunction: () => {};
    private __isClosing: boolean = false;
    private _closingHandler: number;
    private _isBlocked: boolean = false;
    private _openingHandler: number;
    private _isOpening: boolean = false;
    private _isOpened: boolean = false;
    private _isMouseOver: boolean = false;

    public get isMouseOver(): boolean {
        return this._isMouseOver;
    }

    private get _isClosing(): boolean {
        return this.__isClosing;
    }

    private set _isClosing(val: boolean) {
        this.__isClosing = val;
        if (this._popup)
            this._popup.element.classList.toggle("closing", val);
    }

    public set isBlocked(val: boolean) {
        if (this._isBlocked != val) {
            this._isBlocked = val;
            if (this._popup && this._popup.element)
                this._popup.element.classList.toggle("blocked", val);
            if (val)
                this._cancelClosing();
        }
    }
    public get isBlocked(): boolean {
        return this._isBlocked;
    }

    public get calendarItemData(): ICalendarItemData {
        return this._calendarItemData;
    }

    protected override _constructor(): void {
        super._constructor();
        this._visible = false;
    }

    /**
     * Spustí timeout pro zavření tooltipu.
     */
    private _startClosing(): void {
        if (this._isOpening)
            this._cancelOpening();
        if (this._isOpened && !this.isBlocked) {
            this._closingHandler = self.setTimeout(() => this.close(), this._closingTimeout);
            this._isClosing = true;
        }
    }

    /**
     * Spustí timer pro zobrazení tooltipu
     */
    private _startOpening(startElement: HTMLElement, event: Event): void {
        if (this._openingHandler)
            clearTimeout(this._openingHandler);
        this._openingHandler = self.setTimeout(() => this.show(startElement, event), this._openingTimeout);
        this._isOpening = true;
    }

    /**
     * Zablokuje tooltip, tak že zůstane otevřený i při odjetí kurzorem.
     */
    public block(): void {
        this.isBlocked = true;
    }

    /**
     * Umožní tooltipu, aby se zavřel s opuštěním kurzoru myši
     */
    public release(force?: boolean): void {
        this.isBlocked = false;
        if (force) {
            this._isClosing = true;
            this.close();
        } else
            this._startClosing();
    }

    /**
     * Přeruší proces zavírání tooltipu.
     */
    private _cancelClosing(): void {
        this._isClosing = false;
        if (this._closingHandler)
            clearTimeout(this._closingHandler);
    }

    /**
    * Přeruší proces zavírání tooltipu.
    */
    private _cancelOpening(): void {
        if (this._openingHandler)
            clearTimeout(this._openingHandler);
        this._isOpening = false;
    }

    public init(parentContainer: HTMLElement, calItem: ICalendarItemData, tooltipCreator: ICalendarTooltipCreator, canShowTooltipFunction: () => {}): void {
        this._calendarItemData = calItem;
        this._tooltipCreator = tooltipCreator;
        this._canShowTooltipFunction = canShowTooltipFunction;
        this._parentContainer = parentContainer;
        this._parentContainer.addEventListener("mouseenter", this._eventListFnc["mouseenter"] = (e: MouseEvent) => {
            if (this._isClosing) //uživatel najel myší zpět na zavírající se tooltip
                this._cancelClosing();
            else if ((this._canShowTooltipFunction && this._canShowTooltipFunction()) || !this._canShowTooltipFunction)
                this._startOpening(this._parentContainer, e);
        });

        this._parentContainer.addEventListener("mouseleave", this._eventListFnc["mouseleave"] = (_e: MouseEvent) => {
            this._startClosing();
        });
    }

    /**
    * Zobrazi menu
    * @param startElement Uvodni element, ze ktereho se vypocitava zobrazovana poloha, pokud je null, bere se to z udalosti
    * @param event Udalost, ktera predchazi zobrazeni menu
    */
    public async show(startElement: HTMLElement, event: Event): Promise<void> {
        if (startElement == null) {
            logger.error("Element of origin must be passed.");
            return;
        }
        if (!this._tooltipContent)
            this._tooltipContent = await this._tooltipCreator.createCalendarTooltip(this);

        if (event)
            EventHelper.stopPropagation(event);
        if (!this._popup) {
            this._popup = new PopupMenu(this._tooltipContent, true, "auto", "auto", "0", "0");
            this._popup.element.classList.add("calendar-tooltip");
            this._popup.element.style.overflow = "visible";
            if (!this._eventListFnc["popup_mouseenter"]) {
                this._popup.element.addEventListener("mouseenter", this._eventListFnc["popup_mouseenter"] = (_e: MouseEvent) => {
                    this._isMouseOver = true;
                    this._cancelClosing();
                });
            }
            if (!this._eventListFnc["popup_mouseleave"]) {
                this._popup.element.addEventListener("mouseleave", this._eventListFnc["popup_mouseleave"] = (_e: MouseEvent) => {
                    this._isMouseOver = false;
                    this._startClosing();
                });
            }
        }
        this.visible = true;

        this.recalculatePosition(startElement);

        // při změně velikosti okna se přepočítá i pozice popupu, aby zůstal u svého elementu
        window.addEventListener("resize", this._windowResizeListener = () => this.recalculatePosition(startElement));

        this.opened.trigger(this, undefined);
        this._isOpened = true;
        this._isOpening = false;
    }

    /**
     * Přepočítá pozici a nastaví ji popupu.
     */
    public recalculatePosition(element: HTMLElement): void {
        const parentBoundingRectangle = element.getBoundingClientRect();
        const popupBoundingRectangle = this._popup.element.getBoundingClientRect();

        let shiftX = parentBoundingRectangle.left;
        let shiftY = parentBoundingRectangle.top;

        if (parentBoundingRectangle.left + popupBoundingRectangle.width > window.innerWidth - this._showOffsetX) {
            shiftX = parentBoundingRectangle.left + parentBoundingRectangle.width - popupBoundingRectangle.width;
            if (shiftX < 0)
                shiftX = this._showOffsetX;
        }

        if (parentBoundingRectangle.top + popupBoundingRectangle.height > window.innerHeight - this._showOffsetY) {
            shiftY = parentBoundingRectangle.top + parentBoundingRectangle.height - popupBoundingRectangle.height;
        }

        this._popup.setIndent(shiftY + this._showOffsetY + "px", shiftX + this._showOffsetX + "px");
    }

    /**
     * Zavre akutalni a vsecha podrizene nabidky
     */
    public close(): void {
        if (!this._isClosing)
            return;

        if (this._windowResizeListener) {
            window.removeEventListener("resize", this._windowResizeListener);
            this._windowResizeListener = null;
        }

        this.visible = false;
        this._isClosing = false;
        this._disposePopup();
        this._isOpened = false;
        this.closed.trigger(this, undefined);
    }

    public setVisibility(val: boolean): void {
        if (this._visible != val) {
            this._visible = val;

            if (this._popup)
                this._popup.setVisibility(val);
        }
    }

    private _disposePopup(): void {
        if (this._popup) {
            this._popup.element.removeEventListener("mouseenter", this._eventListFnc["popup_mouseenter"]);
            this._popup.element.removeEventListener("mouseleave", this._eventListFnc["popup_mouseleave"]);
            this._popup.setVisibility(false);
            this._popup.dispose();
            this._popup = null;
            this._eventListFnc["popup_mouseenter"] = undefined;
            this._eventListFnc["popup_mouseleave"] = undefined;
        }
    }

    public override dispose(): void {
        this._disposePopup();

        if (this._parentContainer) {
            this._parentContainer.removeEventListener("mouseenter", this._eventListFnc["mouseenter"]);
            this._parentContainer.removeEventListener("mouseleave", this._eventListFnc["mouseleave"]);
            this._eventListFnc["mouseenter"] = undefined;
            this._eventListFnc["mouseleave"] = undefined;
        }
    }
}

registerCustomElement("tsc-CalendarTooltip", CalendarTooltip);
